import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Truck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(location.state?.from?.pathname || "/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[#F4F5F7] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-md p-8">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="bg-[#FF6B00] p-2 rounded-sm">
            <Truck className="h-5 w-5 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-black text-zinc-900">
            SSH <span className="text-[#FF6B00]">AUTOSALES</span>
          </span>
        </div>
        <h1 className="font-display text-2xl font-black uppercase text-zinc-900 mb-1">Admin Portal</h1>
        <p className="text-sm text-zinc-500 mb-6">Sign in to manage inventory and leads.</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-600 text-sm rounded-sm px-4 py-3 mb-4" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email</Label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white border-zinc-300 text-zinc-800 focus-visible:ring-[#FF6B00] rounded-sm"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Password</Label>
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-white border-zinc-300 text-zinc-800 focus-visible:ring-[#FF6B00] rounded-sm"
              data-testid="login-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm"
            data-testid="login-form-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>
        </form>

        <Link
          to="/forgot-password"
          className="block text-center text-xs text-zinc-500 hover:text-[#FF6B00] mt-5 transition-colors"
          data-testid="forgot-password-link"
        >
          Forgot password?
        </Link>
      </div>
    </div>
  );
}
